/**
 * Candidate Agents — backend-neutral bridge for LLM-internal routing (model B)
 *
 * The runtime registers a set of *candidate sub-agents* with the SDK so the
 * entry/main agent's LLM can delegate to them internally via the SDK's native
 * subagent mechanism:
 *
 *   - Claude  `Options.agents: Record<string, AgentDefinition>`
 *   - OpenAI  `Agent.handoffs: Agent[]`
 *   - ADK     `LlmAgent.subAgents: BaseAgent[]`
 *
 * `CandidateAgent` is the common, SDK-independent contract. Each adapter maps it
 * onto its provider's native shape. The selection rule (which agents become
 * candidates) lives here so all adapters stay backend-agnostic.
 */

import type { ResolvedToolchain } from "./bound-resolver.js";
import type { AgentContractLike } from "./task-runner.js";

/**
 * SDK-independent description of a sub-agent the entry agent's LLM may delegate
 * to. Adapters translate this into their provider's native subagent shape.
 */
export interface CandidateAgent {
  /** Agent id (used as the SDK subagent key / name). */
  name: string;
  /** When to use this agent — the routing hint shown to the delegating LLM. */
  description: string;
  /** The agent's system prompt (role / purpose / responsibilities / constraints / rules). */
  prompt: string;
  /** Optional allowed tool names (omit to inherit from the parent agent). */
  tools?: string[];
  /** Optional model id (omit to inherit from the parent agent). */
  model?: string;
}

/**
 * Render an agent contract into its system-prompt string: the static
 * Role / Responsibilities / Constraints / Rules sections.
 *
 * Shared by `buildSplitTaskPrompt` (entry agent system prompt) and
 * `buildCandidateAgents` (each candidate's `prompt`) so both stay in sync.
 */
export function renderAgentSystemPrompt(agent: AgentContractLike): string {
  const sections: string[] = [];

  sections.push(`# Role: ${agent.role_name}\n\n${agent.purpose}`);

  if (agent.responsibilities.length > 0) {
    sections.push(
      `## Responsibilities\n\n${agent.responsibilities.map((r) => `- ${r}`).join("\n")}`,
    );
  }

  if (agent.constraints.length > 0) {
    sections.push(
      `## Constraints\n\n${agent.constraints.map((c) => `- ${c}`).join("\n")}`,
    );
  }

  if (agent.rules && agent.rules.length > 0) {
    const ruleLines = agent.rules.map(
      (r) => `- **${r.id}** [${r.severity}]: ${r.description}`,
    );
    sections.push(`## Rules\n\n${ruleLines.join("\n")}`);
  }

  return sections.join("\n\n");
}

/** Derive a concise routing description (first sentence/line) from a purpose. */
function deriveDescription(purpose: string): string {
  const firstLine = purpose.split("\n").map((l) => l.trim()).find((l) => l.length > 0) ?? "";
  // Prefer the first sentence when the line packs several together.
  const sentenceEnd = firstLine.search(/[.!?](\s|$)/);
  if (sentenceEnd !== -1) {
    return firstLine.slice(0, sentenceEnd + 1).trim();
  }
  return firstLine;
}

export interface BuildCandidateAgentsOptions {
  /** The entry/main agent id. Excluded from the candidate set. */
  entryAgentId?: string;
  /**
   * When provided (typically the entry agent's `can_invoke_agents`), restrict
   * candidates to these agent ids. Omit to allow every non-entry agent.
   */
  allowedAgentIds?: readonly string[];
  toolchainsByAgent?: Map<string, ResolvedToolchain[]>;
}

/**
 * Build the candidate sub-agent set from an agent registry.
 *
 * Selection rule: every agent EXCEPT the entry agent; if `allowedAgentIds` is
 * given, further restrict to that allow-list.
 */
export function buildCandidateAgents(
  agentRegistry: Record<string, AgentContractLike>,
  opts?: BuildCandidateAgentsOptions,
): CandidateAgent[] {
  const entryId = opts?.entryAgentId;
  const allowed = opts?.allowedAgentIds;
  const allowSet = allowed ? new Set(allowed) : undefined;

  const candidates: CandidateAgent[] = [];
  for (const [id, agent] of Object.entries(agentRegistry)) {
    if (id === entryId) continue;
    if (allowSet && !allowSet.has(id)) continue;

    const agentTools = agent.can_execute_tools ? [...agent.can_execute_tools] : [];
    const derivedToolchains = opts?.toolchainsByAgent?.get(id);
    if (derivedToolchains) {
      for (const tc of derivedToolchains) {
        if (!agentTools.includes(tc.id)) {
          agentTools.push(tc.id);
        }
      }
    }

    candidates.push({
      name: id,
      description: deriveDescription(agent.purpose),
      prompt: renderAgentSystemPrompt(agent),
      ...(agentTools.length > 0 ? { tools: agentTools } : {}),
    });
  }
  return candidates;
}
